import { Flex, Form, FormInstance, Select } from 'antd'
import { memo, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import Text from 'shared/components/typography/Text'
import { formatDate } from 'streamer/src/shared/utils/toHHMMSS.ts'
import { FormValues } from '../../types'
import { formKeys } from '../DonationForm/constants'
import { lang } from './lang'
import s from './s.module.css'
import ProgressBar from './ProgressBar'
import Label from '@/shared/components/Label'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext.tsx'

type Props = {
  form: FormInstance<FormValues>
}

function Goal({ form }: Props) {
  const { config } = useStreamerConfigContext()

  const goals = config?.goals

  const goalId = Form.useWatch<FormValues[typeof formKeys.goal]>(formKeys.goal, form)

  const labelOptions = useMemo(() => {
    if (!goals) {
      return []
    }

    return goals.map(goal => ({ label: goal?.description, value: goal?.id }))
  }, [goals])

  const options = useMemo(() => {
    return [
      ...labelOptions,
      {
        label: (
          <Text>
            <FormattedMessage {...lang.without} />
          </Text>
        ),
        value: 'None',
      },
    ]
  }, [labelOptions])

  const foundGoal = useMemo(() => {
    if (goalId === 'None') {
      return
    }

    return goals?.find(item => item.id === goalId)
  }, [goalId, goals])

  if (!goals || goals.length === 0) {
    return null
  }

  return (
    <Flex vertical gap={8}>
      <Flex vertical gap={4}>
        <Label>
          <FormattedMessage {...lang.title} />
        </Label>
        <Form.Item name={formKeys.goal} noStyle>
          <Select size="large" options={options} />
        </Form.Item>
      </Flex>
      {foundGoal && (
        <Flex vertical gap={10} className={s.container}>
          {foundGoal?.deadline_at && new Date(foundGoal.deadline_at).getTime() > Date.now() && (
            <Flex gap={4}>
              <Text fontType="body" fontSize={14}>
                <FormattedMessage {...lang.availableUntil} />
              </Text>
              <Text fontType="body" fontSize={14}>
                {formatDate(foundGoal?.deadline_at)}
              </Text>
            </Flex>
          )}
          <ProgressBar
            currency={config?.base_currency || 'USD'}
            target={foundGoal?.target}
            current={foundGoal?.current}
          />
        </Flex>
      )}
    </Flex>
  )
}

export default memo(Goal)
